/** @jsx jsx */
import { jsx } from '@emotion/core';

import { FunctionComponent } from 'react';
import { styled } from '@material-ui/core/styles';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  TextField,
  FormHelperText
} from '@material-ui/core';
import Button from '../../buttons/PGE-Button';
import EmailTextField from '../../email-text-field';
import {
  convertValidationRules,
} from '../../../hooks/useFormState';
import { validateEmail } from '../../../util/form-validation';
import theme from '../../../themes/theme';
import routes from '../../../routes';
import Link from '../../text-link';
import { useTranslation } from '../../../hooks/useTranslation';
import { useInvoicePaymentRegistration } from '../../../hooks/invoice-payment-portal/useInvoicePaymentRegistration';
import Dropdown from '../../dropdown';
import PhoneTextField from '../../phone-text-field';
import Backdrop from '../../backdrop/Backdrop';
import colors from '../../../themes/main-colors';

const PREFIX = 'InvoicePaymentLoginForm';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  spanText: `${PREFIX}-spanText`,
  submit:`${PREFIX}-submit`
};

const Root = styled('div')(() => ({
  [`& .${classes.card}`]: {
    padding: '2em 2em',
    [theme.breakpoints.down('sm')]: {
      padding: '4em 2em',
    },
  },
  [`& .${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  [`& .${classes.spanText}`]: {
    fontSize: '1rem',
    color: colors.sparkBlue
  },
}));


const InvoicePaymentRegister: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    installerList,
    installerListLoading,
    registerForm,
    content,
    handleSubmit
  } = useInvoicePaymentRegistration();

  if (installerListLoading) {
      return <Backdrop forceOpen message={t('STILL_LOADING')} />;
  }
  
  return (
    <Root>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography variant={'h1'} css={{ marginBottom: theme.spacing(3)}}>
            {content.get('REGISTER_INVOICE_PAYMENT')}
          </Typography>

          <form noValidate>
            <Grid
              container
              justify="flex-end"
              alignContent="center"
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12}>
                <Dropdown
                  label={content.get('INSTALLER_NAME')}
                  selectionList={installerList}
                  {...registerForm.props('installerPersonId')}
                  onChange={(e: any) => {
                    console.log(e);
                    void registerForm.setValue(
                      'installerPersonId',
                      e.target.value,
                    );
                  }}
                />
                {registerForm.errors.installerPersonId && (
                  <FormHelperText error={true}>
                    {registerForm.errors.installerPersonId}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={'firstName'}
                  label={t('FIRST_NAME')}
                  variant="outlined"
                  inputProps={{ minLength: 1, maxLength: 15 }}
                  style={{ width: '100%' }}
                  {...registerForm.props('firstName')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={'lastName'}
                  label={t('LAST_NAME')}
                  variant="outlined"
                  inputProps={{ minLength: 1, maxLength: 60 }}
                  style={{ width: '100%' }}
                  {...registerForm.props('lastName')}
                />
              </Grid>
              <Grid item xs={12}>
                <EmailTextField
                  margin="none"
                  name="email"
                  label={content.get('REGISTERED_EMAIL_ADDRESS')}
                  variant="outlined"
                  autoComplete={'email'}
                  style={{ width: '100%' }}
                  {...registerForm.props('email')}
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneTextField
                  style={{margin:'0', width:'100%'}}
                  {...registerForm.props('phoneNumber')}
                  label={content.get('PHONE_NUMBER')}
                  inputProps={{ minLength: 1, maxLength: 15 }}
                  data-testid="phoneNumber"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={'pinId'}
                  label={content.get('PIN_ID')}
                  variant="outlined"
                  inputProps={{ minLength: 1, maxLength: 60 }}
                  style={{ width: '100%' }}
                  onFocus={() => {
                    const prefix = `${registerForm.values.firstName?.[0] ?? ''}${registerForm.values.lastName?.[0] ?? ''}-`;
                    if (!registerForm.values.pinId?.startsWith(prefix)) {
                      void registerForm.setValue('pinId', prefix + (registerForm.values.pinId || ''));
                    }
                  }}
                 {...registerForm.props('pinId')}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" className={classes.spanText}>{content.get('UNIQUE_PIN_HELPER_TEXT')}</Typography>
              </Grid>
            </Grid>
            <Box
              display="flex"
              justifyContent="space-between"
              css={{ marginTop: theme.spacing(3) }}
            >
              <Link
                plain
                to={routes.INVOICE_PAYMENT_PORTAL_SIGN_IN}
                variant={'body2'}
              >
                {content.get('BACK_TO_SIGNIN')}
              </Link>
              <Button
                className={classes.submit}
                type={'submit'}
                variant={'contained'}
                color={'primary'}
                onClick={registerForm.submit(handleSubmit)}
              >
                {content.get('SUBMIT')}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Root>
  );
};

export default InvoicePaymentRegister;

const createValidateFunction = () =>
  convertValidationRules<{ email: string }>(context => {
    const { values } = context;

    return {
      email: validateEmail,
    };
  });
